import HashTable from './hashtable';
import { BufferGeometry, Mesh, Vector3 } from 'three';
import { get_global_id } from './JCore/jcore';
import { Edge, Face, Vertex, Body } from '@phi/jcore';
import { point_point_distance } from './vector3';
import { current_scene_raynode_for_sketch_objects, edge_is_for_sketch, vertex_is_for_sketch } from './reference_sketches';
import { mustNotDisplayVertex } from './lattice';
import { uil_is_object_visible, uil_set_face_color } from './ui_logic';
import { create_face_material, hexToRgb } from './colours_materials_appearance';
import { console_log } from './utility';

/** A face triangle data structure */
export class Uid_face_triangle {
    index1: number;
    index2: number;
    index3: number;

    constructor(i1: number, i2: number, i3: number) {
        this.index1 = i1;
        this.index2 = i2;
        this.index3 = i3;
    }
}

/** A REFERENCE face triangle data structure
 * i1, i2 and i3 are integer indices of vertices in the reference vertex array.
 * v1, v2 and v3 are THREE.Vector3 objects containing the vertex coordinates.
 */
export class Uid_ref_face_triangle {
    index1: number;
    index2: number;
    index3: number;
    vertex1: Vector3;
    vertex2: Vector3;
    vertex3: Vector3;

    constructor(i1: number, i2: number, i3: number, v1: Vector3, v2: Vector3, v3: Vector3) {
        this.index1 = i1;
        this.index2 = i2;
        this.index3 = i3;
        this.vertex1 = new Vector3();
        this.vertex2 = new Vector3();
        this.vertex3 = new Vector3();
        if (v1)
            this.vertex1.copy(v1);
        if (v2)
            this.vertex2.copy(v2);
        if (v3)
            this.vertex3.copy(v3);
    }
}

/** A REFERENCE face vertex data structure
 * i is the index of the vertex in the reference vertex array.
 * v is the actual THREE.Vector3 object containing the vertex coordinates.
 */
export class Uid_ref_face_vertex {
    vertex: Vector3;
    vindex: number;
    uid_vertex: null | Uid_vertex;

    constructor(i: number, v: Vector3) {
        this.vindex = i;
        this.vertex = new Vector3(v.x, v.y, v.z);
        this.uid_vertex = null;
    }

    set_uid_vertex(uidv: Uid_vertex) {
        this.uid_vertex = uidv;
    }
}

/** An abstract REFERENCE object class and its derived classes for face, edge, vertex */
export class Uid_ref_object<T extends Face | Vertex | Edge> {
    onshape_id: unknown;
    onshape_body_id: unknown;
    source: 'partstudio' | 'stl' | 'parasolid';
    uid_object: Uid_object<T> | null;
    id: number;

    constructor(onshape_id: unknown, onshape_body_id: unknown, source: 'partstudio' | 'stl' | 'parasolid') {
        this.onshape_id = onshape_id;
        this.uid_object = null;
        this.onshape_body_id = onshape_body_id;
        this.id = get_global_id();
        this.source = source; // 'partstudio', 'stl' or 'parasolid'
    }

    set_uid_object(uidobj: Uid_object<T>) {
        this.uid_object = uidobj;
    }
}

export class Uid_ref_vertex extends Uid_ref_object<Vertex> {
    vertex: unknown;
    edges: Array<unknown>;

    constructor(onshape_id: unknown, v: unknown, onshape_body_id = 0, source: 'stl' | 'partstudio' | 'parasolid') {
        super(onshape_id, onshape_body_id, source);
        this.vertex = v;
        this.edges = [];
    }

    add_edge(e: unknown) {
        if (!(this.edges.includes(e)))
            this.edges.push(e);
    }
}

export class Uid_ref_edge extends Uid_ref_object<Edge> {
    points: Array<Vector3>;
    faces: Array<unknown>;
    vertices: Array<unknown>;
    v0: Vector3 | null;
    v1: Vector3 | null;
    onshape_edge_data_json_string: string | null;

    constructor(onshape_id: unknown, onshape_body_id = 0, source: 'stl' | 'partstudio' | 'parasolid') {
        super(onshape_id, onshape_body_id, source);
        this.points = [];
        this.faces = [];
        this.vertices = [];
        this.v0 = null;
        this.v1 = null;
        this.onshape_edge_data_json_string = null;
    }

    add_vertex(v: unknown) {
        if (!(this.vertices.includes(v)))
            this.vertices.push(v);
    }

    add_face(f: unknown) {
        if (!(this.faces.includes(f)))
            this.faces.push(f);
    }

    set_edge_points_array(arr: Array<Vector3>) {
        if (this.vertices.length != 0) {
            const vertex1 = uid_reference_vertices.getItem(this.vertices[0]).v;
            const vertex2 = uid_reference_vertices.getItem(this.vertices[1]).v;
            if (point_point_distance(vertex1, arr[0]!) > point_point_distance(vertex1, arr[arr.length - 1]!))
                arr.reverse();
        }
        // Add all points, skipping duplicates. We identify duplicates as having a distance less than 1e-6
        // An exception if there's only 2 points (a 0-length edge)
        if (arr.length > 2) {
            for (let i = 0; i < arr.length; i++) {
                if (i == 0 || arr[i]!.distanceTo(arr[i-1]!) > 1e-6)
                    this.points.push(arr[i]!);
            }
        } else for (const x of arr) this.points.push(x);
    }

    reverse_edge_points_array(arr: unknown) {
        this.points.reverse();
    }

    set_v0(v: Vector3) {
        this.v0 = new Vector3();
        this.v0.copy(v);
    }

    set_v1(v: Vector3) {
        this.v1 = new Vector3();
        this.v1.copy(v);
    }

    set_onshape_edge_data_json_string(str: string) {
        this.onshape_edge_data_json_string = str;
        // Note: To handle the data object use: JSON.parse(str);
    }

}

export class Uid_ref_face extends Uid_ref_object<Face> {
    triangles: unknown;
    triangle_vertices: unknown;
    edges: Array<Edge>;
    normal: Vector3;
    orientation: unknown;

    constructor(onshape_id: unknown, triangle_vertices: unknown, triangles: unknown, normal: null | Vector3, orientation: unknown, onshape_body_id = 0, source: 'stl' | 'partstudio'|'parasolid') {
        super(onshape_id, onshape_body_id, source);
        this.triangles = triangles;
        this.triangle_vertices = triangle_vertices;
        this.edges = [];
        this.normal = new Vector3();
        this.orientation = (orientation === null ? true : orientation);
        if (normal)
            this.normal.copy(normal);
    }

    add_edge(e: Edge) {
        if (this.edges.indexOf(e) == -1)
            this.edges.push(e);
    }
}

export class Uid_tmp_ref_edge {
    id: number;
    v1id: 0 | Vertex;
    v2id: 0 | Vertex;
    points: Array<Vector3>;

    constructor(id: number) {
        this.id = id;
        this.v1id = 0;
        this.v2id = 0;
        this.points = [];
    }
}

/** An abstract ui_body class */
export class Uid_body {
    id: Body;
    is_open: boolean;
    faces: HashTable<Face, unknown>;
    edges: HashTable<Edge, unknown>;
    vertices: HashTable<Vertex, unknown>;

    constructor(id: Body, is_open: boolean) {
        this.id = id;
        this.is_open = is_open;
        this.faces = new HashTable();
        this.edges = new HashTable();
        this.vertices = new HashTable();
    }
}

type UIDObjectType<T extends Face | Edge | Vertex> =
    T extends Face ? 'face'
    : T extends Edge ? 'edge'
    : T extends Vertex ? 'vertex'
    : never;

/**  An abstract ui_object class and its derived classes for vertex, edge, face */
export class Uid_object<T extends Face | Edge | Vertex> {
    id: T;
    type: UIDObjectType<T>;
    color_r: number;
    color_g: number;
    color_b: number;
    o3d_id: number | null;
    object3d: Mesh | null;

    constructor(id: T, type: UIDObjectType<T>, color_r: number, color_g: number, color_b: number) {
        this.id = id;
        this.o3d_id = null;
        this.color_r = color_r;
        this.color_g = color_g;
        this.color_b = color_b;
        this.type = type;
        this.object3d = null;
    }

    getObject3D() {
        return this.object3d;
    }

    getMaterial() {
        if (!this.object3d) return;
        else if (Array.isArray(this.object3d.material))
            return this.object3d.material[0];
        else
            return this.object3d.material;
    }

    getMirrorMaterial() {
        if (!this.object3d) return;
        if (Array.isArray(this.object3d.material))
            return this.object3d.material[2];
        else
            return null;
    }

    getInnerMaterial() {
        if (!this.object3d) return;
        else if (Array.isArray(this.object3d.material))
            return this.object3d.material[1];
        else
            return null;
    }

    getMirrorInnerMaterial() {
        if (!this.object3d) return;
        else if (Array.isArray(this.object3d.material))
            return this.object3d.material[3];
        else
            return null;
    }

}

export class Uid_vertex extends Uid_object<Vertex> {
    constructor(id: Vertex, r: number, g: number, b: number) {
        super(id, 'vertex', r, g, b);
    }

    setObject3d(o3d: Mesh, is_reference: boolean, dont_show_reference = false, source_if_reference: 'stl' | 'partstudio' | 'parasolid' | undefined = undefined) {
        // Set object3d inside ui_object data structure, and update association table.
        if (this.object3d) {
            if (is_reference) {
                if (source_if_reference == 'stl')
                    uid_ref_stl_vertex_raynode.remove(this.object3d);
                else if (source_if_reference == 'parasolid')
                    uid_ref_parasolid_vertex_raynode.remove(this.object3d);
                else if (source_if_reference == 'partstudio')
                    uid_ref_partstudio_vertex_raynode.remove(this.object3d);
            } else
                uid_objects_raynode.remove(this.object3d);
            this.object3d.geometry.dispose();
            if (Array.isArray(this.object3d.material))
                for (const x of this.object3d.material)
                    x.dispose();
            else
                this.object3d.material.dispose();
            this.object3d = null;
        }

        this.object3d = o3d;
        this.o3d_id = o3d.id;
        uid_object3dIDToUid_object.setItem(this.o3d_id, this);
        // Add to raytracing
        if (is_reference) {
            if (!dont_show_reference) {
                if (source_if_reference == 'stl')
                    uid_ref_stl_vertex_raynode.add(this.object3d);
                else if (source_if_reference == 'parasolid')
                    uid_ref_parasolid_vertex_raynode.add(this.object3d);
                else if (source_if_reference == 'partstudio')
                    uid_ref_partstudio_vertex_raynode.add(this.object3d);
            }
        } else if (vertex_is_for_sketch(this.id)) {
            if (!mustNotDisplayVertex(this.id))
                current_scene_raynode_for_sketch_objects().add(this.object3d);
        } else {
            if (!mustNotDisplayVertex(this.id))
                uid_objects_raynode.add(this.object3d);
        }
    }

    freeVertex() {
        if (!this.object3d) return;
        this.object3d.geometry.dispose();
        if (Array.isArray(this.object3d.material))
            for (const x of this.object3d.material)
                x.dispose();
        else
            this.object3d.material.dispose();
    }

}

export class Uid_edge extends Uid_object<Edge> {
    is_linear: boolean;
    visible_edge_mesh: Mesh | null;

    constructor(id: Edge, r: number, g: number, b: number) {
        super(id, 'edge', r, g, b);
        this.is_linear = false;
        this.visible_edge_mesh = null;
    }

    setObject3d(o3d: Mesh, is_reference: boolean, source_if_reference: 'partstudio' | 'stl' | 'parasolid') {
        // Set object3d inside ui_object data structure, and update association table.

        if (this.object3d) {
            if (is_reference) {
                if (source_if_reference == 'partstudio')
                    uid_ref_partstudio_edges_raynode.remove(this.object3d);
                else if (source_if_reference == 'parasolid')
                    uid_ref_parasolid_edges_raynode.remove(this.object3d);
                else if (source_if_reference == 'stl')
                    uid_ref_stl_edges_raynode.remove(this.object3d);
            } else if (edge_is_for_sketch(this.id))
                current_scene_raynode_for_sketch_objects().remove(this.object3d);
            else {
                uid_objects_raynode.remove(this.object3d);
            }
            this.object3d.geometry.dispose();
            if (Array.isArray(this.object3d.material))
                for (const x of this.object3d.material)
                    x.dispose();
            else
                this.object3d.material.dispose();
            this.object3d = null;
        }

        this.object3d = o3d;
        this.o3d_id = o3d.id;
        uid_object3dIDToUid_object.setItem(this.o3d_id, this);
        // Add to raytracing
        if (is_reference) {
            if (source_if_reference == 'partstudio')
                uid_ref_partstudio_edges_raynode.add(o3d);
            else if (source_if_reference == 'parasolid')
                uid_ref_parasolid_edges_raynode.add(o3d);
            else if (source_if_reference == 'stl')
                uid_ref_stl_edges_raynode.add(o3d);
        } else if (edge_is_for_sketch(this.id) && uil_is_object_visible(this.id))
            current_scene_raynode_for_sketch_objects().add(o3d);
        else if (uil_is_object_visible(this.id))
            uid_objects_raynode.add(o3d);
    }

    set_linear_flag(b: boolean) {
        this.is_linear = b;
    }

    get_linearity_flag() {
        return this.is_linear;
    }

    set_visible_edge_mesh(visible_edge_mesh: Mesh, is_reference: boolean, source_if_reference: unknown) {
        const uido = this.o3d_id && uid_object3dIDToUid_object.getItem(this.o3d_id);
        const mesh = uido instanceof Uid_edge && uido.visible_edge_mesh;
        if (mesh)
            uid_visible_edge_mesh_raynode.remove(mesh);

        this.visible_edge_mesh = visible_edge_mesh;
        if (is_reference)
            uid_ref_partstudio_edges_visible_raynode.add(visible_edge_mesh);
        else if (uil_is_object_visible(this.id))
            uid_visible_edge_mesh_raynode.add(visible_edge_mesh);
    }

    get_visible_edge_mesh() {
        return this.visible_edge_mesh;
    }

    remove_visible_edge_mesh(is_reference: boolean) {
        if (!this.visible_edge_mesh)
            return;
        if (is_reference) {
            const re = uid_reference_edges.getItem(this.id);
            if (re.source == 'partstudio')
                uid_ref_partstudio_edges_visible_raynode.remove(this.visible_edge_mesh);
            if (re.source == 'parasolid')
                uid_ref_parasolid_edges_visible_raynode.remove(this.visible_edge_mesh);
            if (re.source == 'stl')
                uid_ref_stl_edges_visible_raynode.remove(this.visible_edge_mesh);
        } else
            uid_visible_edge_mesh_raynode.remove(this.visible_edge_mesh);

        this.visible_edge_mesh.geometry.dispose();
        if (Array.isArray(this.visible_edge_mesh.material))
            for (const x of this.visible_edge_mesh.material)
                x.dispose();
        else
            this.visible_edge_mesh.material.dispose();
    }

    freeEdge() {
        if (!this.object3d)
            return;
        if (this.object3d.geometry)
            this.object3d.geometry.dispose();
        if (Array.isArray(this.object3d.material))
            for (const x of this.object3d.material)
                x.dispose();
        else if (this.object3d.material)
            this.object3d.material.dispose();
        this.remove_visible_edge_mesh(false);
        // @ts-ignore
        renderer.renderLists.dispose();
        this.object3d = null;
    }

}

export class Uid_face extends Uid_object<Face> {
    material_name: string;

    constructor(id: Face, colorhex: string) {
        super(id, 'face', hexToRgb(colorhex).r, hexToRgb(colorhex).g, hexToRgb(colorhex).b);
        this.material_name = uid_default_face_material_name;
        uid_face_colors.setItem(this.id, colorhex);
    }

    setObject3d(o3d: Mesh, source_if_reference: unknown) {
        // Set object3d inside ui_object data structure, and update association table.
        this.freeFace();
        this.object3d = o3d;
        this.o3d_id = o3d.id;
        uid_object3dIDToUid_object.setItem(this.o3d_id, this);
    }

    setMaterial(material_name: string) {
        if (this.object3d && this.object3d.material) {
            const material = create_face_material(
                this.id,
                material_name,
                false,
                // @ts-ignore
                this.object3d.material.wireframe,
            );
            if (Array.isArray(this.object3d.material))
                this.object3d.material[0] = material;
            else
                this.object3d.material = material;
        }
        this.material_name = material_name;
        uid_face_materials.setItem(this.id, material_name);
    }

    setColor(colorhex: string, send_to_core: boolean) {
        if (!colorhex)
            return;
        const rgb = hexToRgb(colorhex);
        if (!rgb || isNaN(rgb.r) || isNaN(rgb.g) || isNaN(rgb.b))
            return;
        this.color_r = rgb.r;
        this.color_g = rgb.g;
        this.color_b = rgb.b;
        if (this.object3d && this.object3d.material) {
            if (Array.isArray(this.object3d.material))
                // @ts-ignore
                this.object3d.material[0].color.set(colorhex);
            else
                // @ts-ignore
                this.object3d.material.color.set(colorhex);
        }
        uid_face_colors.setItem(this.id, colorhex);
        if (send_to_core) {
            console_log('Sending to core');
            uil_set_face_color([this.id], colorhex);
        }
    }

    freeFace() {
        if (!this.object3d)
            return;

        uid_objects_raynode.remove(this.object3d);

        if (this.object3d.geometry) {
            this.object3d.geometry.dispose();
            const geometry = this.object3d.geometry;
            if (geometry instanceof BufferGeometry) {
                if (geometry.attributes.position)
                    geometry.deleteAttribute('position');
                if (geometry.attributes.color)
                    geometry.deleteAttribute('color');
                if (geometry.attributes.normal)
                    geometry.deleteAttribute('normal');
                if (geometry.attributes.uv)
                    geometry.deleteAttribute('uv');
                if (geometry.index)
                    geometry.index = null;
            } else if (geometry.faces)
                geometry.faces = [];
        }

        if (this.object3d.material) {
            this.getMaterial()?.dispose();
            this.getMirrorMaterial()?.dispose();
            this.getInnerMaterial()?.dispose();
            this.getMirrorInnerMaterial()?.dispose();
        }

        this.object3d = null;
    }
}
